import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useModal } from '../../../../hooks/useModal';
import { InputField, SelectField } from '../../../../components/form';
import { useForm } from 'react-hook-form';
import { ModalsKeys } from '../../../../global';
import { AddLocationFormInputs } from 'modules/aftermarketPartner/types';
import { useMutation } from '@apollo/client';
import { UPSERT_LOCATION } from 'modules/aftermarketPartner/api';
import { useNotifications } from 'modules/notification';

export const AddLocationDialog = () => {
  const { showNotification } = useNotifications();
  const { isOpen, data, handleClose } = useModal(ModalsKeys.AftermarketPartnerAddLocationDialog);

  const { accountDynamicsId, dealerContacts, refetchDealerAccount } = data;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [upsertLocation] = useMutation(UPSERT_LOCATION);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AddLocationFormInputs>({
    defaultValues: {
      locationName: '',
      ocaContactId: undefined,
    },
  });

  const handleSave = async (formData: AddLocationFormInputs) => {
    try {
      setIsLoading(true);
      await upsertLocation({
        variables: {
          input: {
            accountDynamicsId,
            category: 'Region', // hardcode for now
            ...formData,
          },
        },
      });
      await refetchDealerAccount();
      handleClose();
      showNotification('Saved!');
    } catch (error) {
      showNotification('An error occurred', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const dealerContactsOptions =
    dealerContacts
      ?.filter(contact => contact.status !== 'blocked')
      ?.map(contact => ({
        label: contact.fullName,
        value: contact.id,
      })) ?? [];

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        <Typography component="span" variant="h5" style={{ fontWeight: 500 }}>
          Add Location
        </Typography>
      </DialogTitle>
      {!isLoading ? (
        <>
          <DialogContent>
            <Stack gap="1rem">
              <InputField
                control={control}
                label="Location Name"
                name="locationName"
                rules={{
                  required: true,
                }}
                inputProps={{
                  required: true,
                  error: !!errors.locationName,
                  helperText: errors.locationName && 'Required',
                }}
              />
              <SelectField
                control={control}
                label="Primary Contact"
                name="ocaContactId"
                options={dealerContactsOptions}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit(handleSave)} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </>
      ) : (
        <LinearProgress style={{ width: '100%' }} />
      )}
    </Dialog>
  );
};
